import React from 'react';
import {
  AssetMetaInterface,
  AutoFillArgs,
  AutoFillResponse,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {BaseAssistant} from './BaseAssistant';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';
import {AutoFlowError} from '@taopix/taopix-core';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useErrorBoundary} from 'react-error-boundary';
import {useTaopix} from '../../Context/TaopixContext';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';

type AdvancedAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  onAfterRun: () => void;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

export const PictureSelectionOnlyAssistant = ({...props}: AdvancedAssistantProps) => {
  const taopix = useTaopix();
  const userFeedback = useUserFeedback();
  const {experience} = useExperience();
  const {showBoundary} = useErrorBoundary();
  const stateManager = taopix.core.get('stateManager');

  const getNextButton = (): React.ReactElement => {
    return React.cloneElement(props.nextButton, {
      ...props.nextButton.props,
      onClick: () => {
        if (typeof props.onContinueInEditor === 'function') {
          props.onContinueInEditor();
        }
      },
    });
  };

  const run = (): Promise<(ReducerState & AutoFillResponse) | boolean | void> => {
    if (!experience.WIZARD.pictureSelectionOnlyOptions.autoFlowSelectedPicturesAtProjectStart) {
      return new Promise((resolve, reject) => {
        resolve(true);
      });
    }

    const args: AutoFillArgs = {
      save: true,
      showLoading: false,
      createPreviewThumbnail: false,
      coverItems: [],
      checkImageUsage: false,
      fromCreation: true,
      sortOrder: experience.WIZARD.addPicturesStep.defaultSortOrder,
      sortDirection: 'asc',
      includeCovers: true,
    };

    return stateManager
      .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', args)
      .then(response => {
        return response;
      })
      .catch(error => {
        if (error instanceof AutoFlowError) {
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message: error.message,
              showHeader: false,
            })
          );
        } else if (500 === error?.response?.status) {
          // Display our error from the server.
          showBoundary(error.response.data.error);
        } else {
          // Display the Axios error message.
          showBoundary(error);
        }
      })
      .catch(e => {
        console.error(e);
      });
  };

  /**
   * Override the passed onAfterRun function.
   */
  const onAfterRun = (): void => {
    if (experience.WIZARD.pictureSelectionOnlyOptions.autoFlowSelectedPicturesAtProjectStart) {
      // only execute if automatic autoflow is enabled.
      props.onAfterRun();
    }
  };

  return (
    <BaseAssistant run={run} {...props} onAfterRun={onAfterRun} nextButton={getNextButton()} />
  );
};
